// Chartmetric API calls via Vercel serverless proxy (avoids CORS)

export interface CmSpotifyDataPoint {
    timestp: string;
    value: number;
}

function addDays(dateStr: string, days: number): string {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

/**
 * Fetch a single 90-day chunk via the proxy.
 */
async function fetchChunk(
    cmArtistId: string,
    since: string,
    until: string
): Promise<CmSpotifyDataPoint[]> {
    const resp = await fetch(
        `/api/cm-stats?id=${cmArtistId}&since=${since}&until=${until}`
    );
    if (!resp.ok) return [];
    const data = await resp.json();
    return data?.listeners ?? [];
}

/**
 * Fetch Spotify fan metrics time-series via sequential 90-day chunks.
 * Each chunk is edge-cached independently, so repeat loads are instant.
 */
export async function fetchCmSpotifyStats(
    cmArtistId: string,
    since: string,
    until: string
): Promise<CmSpotifyDataPoint[]> {
    const sinceMs = new Date(since).getTime();
    const untilMs = new Date(until).getTime();
    const totalDays = Math.ceil((untilMs - sinceMs) / (1000 * 60 * 60 * 24));

    // Split into 90-day chunks, fetch sequentially (CM rate limits)
    const allPoints: CmSpotifyDataPoint[] = [];
    const seen = new Set<string>();

    for (let offset = 0; offset < totalDays; offset += 90) {
        const chunkStart = addDays(since, offset);
        const chunkEnd = addDays(since, Math.min(offset + 90, totalDays));
        if (new Date(chunkStart) >= new Date(until)) break;

        const chunk = await fetchChunk(cmArtistId, chunkStart, chunkEnd);
        for (const point of chunk) {
            const key = point.timestp?.slice?.(0, 10) || point.timestp;
            if (!seen.has(key)) {
                seen.add(key);
                allPoints.push(point);
            }
        }
    }

    allPoints.sort((a, b) => a.timestp.localeCompare(b.timestp));
    return allPoints;
}

// Cache track counts to avoid repeated API calls
const trackCountCache = new Map<string, number>();

/**
 * Fetch artist track count via our server-side proxy.
 */
export async function fetchCmTrackCount(cmArtistId: string): Promise<number> {
    const cached = trackCountCache.get(cmArtistId);
    if (cached !== undefined) return cached;

    const resp = await fetch(`/api/cm-tracks?id=${cmArtistId}`);
    if (!resp.ok) return 0;

    const data = await resp.json();
    const count = data?.total ?? 0;
    if (count > 0) trackCountCache.set(cmArtistId, count);
    return count;
}
